import React from 'react';
import { useLocalization, type Language } from '../../../config/localization';
import Icon, { ICONS } from '../../ui/icons';
import TokenBalanceCard from '../../ui/displays/TokenBalanceCard';

interface MultiContextProps {
  selectedLanguage: Language;
  /** Model picker supplied by MultiGenerationPage (which owns its state). */
  children?: React.ReactNode;
  /** Cost row + Generate button, pinned to the bottom like on the classic pages. */
  footer?: React.ReactNode;
}

/**
 * Left-panel content for the Multi section: token balance (same block
 * ImageGenerationContext shows on the classic pages) + section intro + the
 * model picker the page hands down. No extra request for the balance —
 * MultiGenerationPage already polls it via useTokenBalancePolling, this only
 * reads the shared context.
 */
export default function MultiContext({ selectedLanguage, children, footer }: MultiContextProps) {
  const t = useLocalization(selectedLanguage);

  return (
    <div className="h-full flex flex-col">
      <TokenBalanceCard selectedLanguage={selectedLanguage} />

      <div className="px-4 py-3 border-b border-gray-700/50 flex-shrink-0">
        <h3 className="text-base font-semibold text-white mb-1 flex items-center gap-2">
          <Icon name={ICONS.SPLIT_ARROWS} size={18} className="text-purple-400" />
          {t.multiGen?.title || 'Multi generation'}
        </h3>
        <p className="text-xs text-gray-400">
          {t.multiGen?.subtitle || 'Pick several models and generate across all of them at once'}
        </p>
      </div>

      {/* Model picker — the only scrolling region, so the balance/intro above and
          the Generate footer below stay pinned (same flex mechanism the classic
          panel uses, not position: sticky). */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {children}
      </div>

      {footer && (
        <div className="p-4 border-t border-gray-700/50 flex-shrink-0">
          {footer}
        </div>
      )}
    </div>
  );
}
